import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceTracker = ({ complianceData, onGenerateReport, onViewDetails }) => {
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  const getComplianceColor = (score) => {
    if (score >= 95) return 'text-success bg-success/10 border-success/20';
    if (score >= 80) return 'text-warning bg-warning/10 border-warning/20';
    return 'text-error bg-error/10 border-error/20';
  };

  const getComplianceIcon = (score) => {
    if (score >= 95) return 'CheckCircle';
    if (score >= 80) return 'AlertTriangle';
    return 'XCircle';
  };

  const filteredData = selectedPlatform === 'all' 
    ? complianceData?.platforms 
    : complianceData?.platforms?.filter(p => p?.id === selectedPlatform);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">DSA Compliance Tracker</h2>
          <p className="text-sm text-muted-foreground">Platform response monitoring and verification</p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={onGenerateReport}
          >
            Export Report
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="RefreshCw"
            onClick={() => window.location?.reload()}
          >
            Refresh
          </Button>
        </div>
      </div>
      {/* Overall Compliance Score */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="col-span-1 md:col-span-2 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-foreground">Overall Compliance</h3>
              <p className="text-sm text-muted-foreground">Across all platforms</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-primary">{complianceData?.overallScore}%</div>
              <div className="text-sm text-primary/80">DSA Compliant</div>
            </div>
          </div>
        </div>
        <div className="bg-success/10 border border-success/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-success">{complianceData?.compliantPlatforms}</div>
          <div className="text-sm text-success/80">Compliant Platforms</div>
        </div>
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-warning">{complianceData?.pendingActions}</div>
          <div className="text-sm text-warning/80">Pending Actions</div>
        </div>
      </div>
      {/* Platform Filter */}
      <div className="flex items-center space-x-2 mb-4">
        <span className="text-sm font-medium text-foreground">Filter by Platform:</span>
        <div className="flex space-x-1">
          <Button
            variant={selectedPlatform === 'all' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setSelectedPlatform('all')}
          >
            All Platforms
          </Button>
          {complianceData?.platforms?.slice(0, 3)?.map((platform) => (
            <Button
              key={platform?.id}
              variant={selectedPlatform === platform?.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedPlatform(platform?.id)}
            >
              {platform?.name}
            </Button>
          ))}
        </div>
      </div>
      {/* Platform Compliance Details */}
      <div className="space-y-4">
        {filteredData?.map((platform) => (
          <div key={platform?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Globe" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">{platform?.name}</h3>
                  <p className="text-sm text-muted-foreground">{platform?.region}</p>
                </div>
              </div>
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${getComplianceColor(platform?.complianceScore)}`}>
                <Icon name={getComplianceIcon(platform?.complianceScore)} size={16} />
                <span className="text-sm font-medium">{platform?.complianceScore}%</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <div className="text-lg font-bold text-success">{platform?.actionsCompleted}</div>
                <div className="text-xs text-muted-foreground">Actions Completed</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-warning">{platform?.actionsPending}</div>
                <div className="text-xs text-muted-foreground">Pending Actions</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-primary">{platform?.avgResponseTime}h</div>
                <div className="text-xs text-muted-foreground">Avg Response</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-foreground">{platform?.lastUpdate}</div>
                <div className="text-xs text-muted-foreground">Last Update</div>
              </div>
            </div>

            {/* Recent Actions */}
            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Recent Actions</h4>
              <div className="space-y-2">
                {platform?.recentActions?.map((action, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted/30 rounded">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={action?.status === 'completed' ? 'CheckCircle' : action?.status === 'pending' ? 'Clock' : 'XCircle'} 
                        size={14} 
                        className={action?.status === 'completed' ? 'text-success' : action?.status === 'pending' ? 'text-warning' : 'text-error'}
                      />
                      <span className="text-sm text-foreground">{action?.type}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-muted-foreground">{action?.timestamp}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onViewDetails(platform?.id, action?.id)}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Compliance Trends */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-lg font-medium text-foreground mb-4">Compliance Trends</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">This Week</span>
              <Icon name="TrendingUp" size={16} className="text-success" />
            </div>
            <div className="text-2xl font-bold text-success">+5.2%</div>
            <div className="text-xs text-muted-foreground">Compliance improvement</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Response Time</span>
              <Icon name="Clock" size={16} className="text-primary" />
            </div>
            <div className="text-2xl font-bold text-primary">2.4h</div>
            <div className="text-xs text-muted-foreground">Average across platforms</div>
          </div>
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Audit Ready</span>
              <Icon name="Shield" size={16} className="text-success" />
            </div>
            <div className="text-2xl font-bold text-success">98%</div>
            <div className="text-xs text-muted-foreground">Documentation complete</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceTracker;